import React, { useContext, useState, useRef, useEffect } from 'react';
import classNames from 'classnames';
import postMonetateImpression from '../../../utils/monetate/postMonetateImpression';
import NavigationPane from '../navigation-pane/NavigationPane';
import notifyAnalytics from '../notify-analytics';
import offersCategory from '../offers-category';
import { isDesktop, isTouchDevice } from '../../../utils/device/identifyDevice';
import EnvVarsFeaturesContext from '../../../scaffold/EnvVarsFeaturesContext';

import styles from './navigation-item.scss';

function getNavigationCategory({ name, url, id, child, overrides }) {
  return {
    name: overrides ? overrides.name : name,
    url: overrides ? overrides.url : url,
    id: overrides ? overrides.id : id,
    child: overrides ? overrides.child : child,
  };
}

function NavigationItem({
  id,
  className,
  name,
  url,
  depth,
  isDisplayedInGlobalNav,
  child = [],
  hierarchy = '',
  backLabel,
  toggleParentOpened,
  fetchAndStoreChildCategories,
  parentOpen,
  openClass = '',
  navigationV2Active,
  scrollToTop,
  isOverrideCategoryChild,
}) {
  const [opened, setOpened] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [managedImpressionSent, setManagedImpressionSent] = useState(false);

  const {
    envVars: { monetate },
    categoryOverrides,
    features: { monetateExperiences },
  } = useContext(EnvVarsFeaturesContext);

  const overrides = categoryOverrides && categoryOverrides[id];
  const navigationCategory = getNavigationCategory({
    name,
    url,
    id,
    child,
    overrides,
  });

  const isOffersCategory = offersCategory(navigationCategory.name, depth);
  const offersChildren = navigationCategory.child
    ? navigationCategory.child.filter(item => !!item.url)
    : [];

  const hasNestedMenu =
    navigationCategory.child && navigationCategory.child.length > 0;
  const navigationPath = `${hierarchy ? `${hierarchy}|` : ''}${
    navigationCategory.name
  }`;

  const toggleOpen = e => {
    e.preventDefault();
    if (fetchAndStoreChildCategories && !isDesktop() && !overrides) {
      fetchAndStoreChildCategories(navigationCategory.id);
    }
    toggleParentOpened(!opened);
    setOpened(!opened);
    scrollToTop();
  };

  const navigate = () => {
    notifyAnalytics(navigationPath);
  };

  const ElementType = navigationCategory.url ? 'a' : 'button';
  const useImage = navigationV2Active && navigationCategory.id && depth === 0;

  const itemClassNames = classNames([
    styles.item,
    styles[`item--level${depth}`],
    className,
    {
      [openClass]: opened,
      [styles.columnar]:
        navigationCategory.child.filter(
          ({ isDisplayedInGlobalNav: desktop }) => desktop === true
        ).length > 11,
      [styles.mobile]: !isDisplayedInGlobalNav,
      [styles.noBorder]: useImage,
    },
  ]);

  const smoothRemove = () => {
    if (isDesktop()) {
      // iPad with desktop nav needs immediate removal
      setOpened(false);
      toggleParentOpened(false);
    } else {
      setRemoving(true);
      toggleParentOpened(false);
      window.setTimeout(() => {
        setOpened(false);
        setRemoving(false);
      }, 600);
    }
  };

  if (!hasNestedMenu && !navigationCategory.url) {
    return null;
  }

  const handleMouseLeave = () => {
    if (isDesktop() && !isTouchDevice()) {
      setOpened(false);
    }
  };

  const innerRef = useRef(null);
  useEffect(() => {
    if (innerRef && innerRef.current) {
      innerRef.current.addEventListener('mouseleave', handleMouseLeave);
    }
  }, []);

  useEffect(() => {
    if (
      opened &&
      navigationCategory.name === 'Home & Garden' &&
      !managedImpressionSent
    ) {
      setManagedImpressionSent(true);
      postMonetateImpression(
        monetate,
        'navigation-experimental',
        monetateExperiences
      );
      window.monetateQ = window.monetateQ || [];
      if (isDesktop() && !isTouchDevice()) {
        window.monetateQ.push([
          'trackEvent',
          ['jl.header.navigation.home-garden-desktop'],
        ]);
      } else {
        window.monetateQ.push([
          'trackEvent',
          ['jl.header.navigation.home-garden-mobile'],
        ]);
      }
    } else if (opened) {
      postMonetateImpression(monetate, 'remove-dont-miss', monetateExperiences);
    }
  }, [opened]);

  return (
    <li
      ref={innerRef}
      className={itemClassNames}
      onMouseEnter={() => isDesktop() && !isTouchDevice() && setOpened(true)}
    >
      <ElementType
        className={classNames([
          styles.link,
          {
            [styles.offersLabel]: hasNestedMenu && isOffersCategory,
            [styles.forward]: hasNestedMenu && !useImage,
            [styles.imageLabel]: useImage,
          },
        ])}
        href={navigationCategory.url}
        onClick={
          hasNestedMenu && (!opened || (isTouchDevice() && depth === 0))
            ? toggleOpen
            : navigate
        }
      >
        {useImage ? (
          <>
            {parentOpen && (
              <img
                src={`https://johnlewis.scene7.com/is/image/johnlewis/header-menu-${navigationCategory.id}?hei=180&qlt=80`}
                alt=""
              />
            )}
            <span className={styles.navWithImageText}>
              {navigationCategory.name}
            </span>
          </>
        ) : (
          navigationCategory.name
        )}
      </ElementType>
      {isOffersCategory && offersChildren.length > 0 && (
        <ul className={styles.offersSubnav}>
          {offersChildren.slice(0, 3).map(subitem => (
            <NavigationItem
              {...subitem}
              key={subitem.id}
              depth={depth}
              hierarchy={navigationPath}
              scrollToTop={scrollToTop}
              isOverrideCategoryChild={isOverrideCategoryChild}
            />
          ))}
          {offersChildren.length > 3 && (
            <li>
              <ElementType
                className={styles.subnavLink}
                href={navigationCategory.url}
                onClick={hasNestedMenu && !opened ? toggleOpen : navigate}
              >
                View more
              </ElementType>
            </li>
          )}
        </ul>
      )}
      {(opened || parentOpen) && hasNestedMenu && (
        <NavigationPane
          backLabel={backLabel}
          goBack={smoothRemove}
          id={navigationCategory.id}
          categoryLink={navigationCategory.url}
          name={navigationCategory.name}
          depth={depth + 1}
          visible={opened && !removing}
          child={navigationCategory.child}
          hierarchy={hierarchy}
          navigationV2Active={navigationV2Active}
          scrollToTop={scrollToTop}
          isOverrideCategoryChild={isOverrideCategoryChild}
        />
      )}
    </li>
  );
}

export default NavigationItem;
